import React, { useContext, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useActor } from '@xstate/react';
import { useRouter, usePathname } from 'next/navigation';
import { GlobalStateContext } from '../../state/app/index';
import { isMobile } from '../../utils/helpers.ts';
import CloudSyncStatus from './CloudSyncStatus';
import NoteEditorToolbar from '../editor/NoteEditorToolbar';
import logger from '../../logger';

const MobileActionsMenu = () => {
  const router = useRouter();
  const pathname = usePathname();
  const globalServices = useContext(GlobalStateContext);
  const [appState, sendAppState] = useActor(globalServices.appState);
  const [, sendPaletteState] = useActor(globalServices.paletteState);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [formattingBarOpen, setFormattingBarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('notes');
  const [mounted, setMounted] = useState(false);
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (keyboardVisible) {
      document.documentElement.classList.add('keyboard-open');
    } else {
      document.documentElement.classList.remove('keyboard-open');
    }
  }, [keyboardVisible]);

  useEffect(() => {
    if (!window.visualViewport) return;
    // Capture baseline at mount — on Android, window.innerHeight can also shrink
    // when the keyboard opens, so comparing against it always yields ~1.0.
    let baselineHeight = window.visualViewport.height;

    const checkKeyboard = () => {
      const current = window.visualViewport.height;
      // Self-update baseline on orientation change (viewport grows larger).
      if (current > baselineHeight) baselineHeight = current;
      setKeyboardVisible(current < baselineHeight * 0.75);
    };

    // Some Android browsers fire visualViewport.resize, others fire window.resize
    // (same split seen in vhFixWithResize). Listen to all three signals so we
    // catch the keyboard open regardless of which one the browser emits.
    // focusin/focusout are a last-resort fallback with a short delay to let the
    // keyboard finish animating before sampling the height.
    const handleFocus = () => setTimeout(checkKeyboard, 300);

    window.visualViewport.addEventListener('resize', checkKeyboard);
    window.addEventListener('resize', checkKeyboard);
    document.addEventListener('focusin', handleFocus);
    document.addEventListener('focusout', handleFocus);

    return () => {
      window.visualViewport.removeEventListener('resize', checkKeyboard);
      window.removeEventListener('resize', checkKeyboard);
      document.removeEventListener('focusin', handleFocus);
      document.removeEventListener('focusout', handleFocus);
    };
  }, []);

  const {
    breakpoint,
    newNoteEntityObject,
    selectedAreaOfFocus,
    selectedNotebookId,
    defaultNotebook,
    showOpenNotesPanel,
    areaOfFocusOpenNoteIds,
  } = appState.context;

  const selectTab = (tabName) => {
    setActiveTab(tabName);
    if (pathname !== '/') router.push('/');
    sendAppState({ type: 'SelectTab', tabName });
  };

  if (!isMobile(breakpoint)) return null;

  const addNote = () => {
    logger.info('MobileActionsMenu - addNote');
    const notebookIdToUse =
      selectedNotebookId === 'all_notes'
        ? defaultNotebook?.notebook_id
        : selectedNotebookId;

    // Navigate first while the machine is still idle — makes the editor appear immediately
    if (pathname !== '/') router.push('/');
    sendAppState({ type: 'SelectTab', tabName: 'editorWrapper' });

    // Create note after navigation so SelectTab is processed before the machine enters creatingNote
    sendAppState({
      type: 'createNote',
      data: {
        noteObject: { ...newNoteEntityObject, notebook_id: notebookIdToUse },
        selectedAreaOfFocus,
        openNotesLength: selectedAreaOfFocus?.order_of_items?.length ?? 0,
        isSelectedAreaOfFocus: false,
      },
    });
  };

  const goHome = () => {
    if (pathname === '/home') {
      router.push('/');
      sendAppState({ type: 'SelectTab', tabName: 'editorWrapper' });
    } else {
      router.push('/home');
    }
  };

  const toggleOpenNotes = () => {
    logger.info('MobileActionsMenu - toggleOpenNotes');
    sendAppState({ type: 'toggleOpenNotesPanelView' });
  };

  const closeSidebar = () => setSidebarOpen(false);

  const sidebarAction = (fn) => () => {
    closeSidebar();
    fn();
  };

  const openNotesCount = areaOfFocusOpenNoteIds?.length ?? 0;

  const formattingBar = mounted && ReactDOM.createPortal(
    <div className={`mobile-formatting-bar${formattingBarOpen ? ' open' : ''}`}>
      <NoteEditorToolbar dropzoneReference={null} viewOnly={false} />
    </div>,
    document.body
  );

  const sidebar = mounted && sidebarOpen && ReactDOM.createPortal(
    <>
      <div className="mobile-sidebar-backdrop" onClick={closeSidebar} />
      <nav className="mobile-sidebar">
        <div className="mobile-sidebar__header">
          <span className="mobile-sidebar__title">Menu</span>
          <CloudSyncStatus />
          <button className="mobile-sidebar__close" onClick={closeSidebar} aria-label="Close menu">
            <i className="material-icons">close</i>
          </button>
        </div>
        <ul className="mobile-sidebar__list">
          {/* Primary navigation */}
          <li
            className={`mobile-sidebar__item${pathname === '/home' ? ' mobile-sidebar__item--active' : ''}`}
            onClick={sidebarAction(goHome)}
          >
            <i className="material-icons">home</i> Home
          </li>
          <li
            className={`mobile-sidebar__item${pathname === '/areas' ? ' mobile-sidebar__item--active' : ''}`}
            onClick={sidebarAction(() => router.push('/areas'))}
          >
            <i className="material-icons">dashboard</i> Areas
          </li>
          {[
            { label: 'Notes', tab: 'notes', icon: 'description' },
            { label: 'Notebooks', tab: 'notebooks', icon: 'menu_book' },
          ].map(({ label, tab, icon }) => (
            <li
              key={tab}
              className={`mobile-sidebar__item${activeTab === tab ? ' mobile-sidebar__item--active' : ''}`}
              onClick={sidebarAction(() => selectTab(tab))}
            >
              <i className="material-icons">{icon}</i> {label}
            </li>
          ))}
          {/* Spacer pushes primary actions to the bottom */}
          <li className="mobile-sidebar__spacer" />

          {/* Primary actions — thumb zone */}
          <li className="mobile-sidebar__divider" />
          <li className="mobile-sidebar__item" onClick={sidebarAction(() =>
            sendAppState({ type: 'SelectTab', tabName: 'info' })
          )}>
            <i className="material-icons">info_outline</i> Note Info
          </li>
          <li className="mobile-sidebar__item">
            <i className="material-icons">settings</i>
            <a href="/settings">Settings</a>
          </li>
          <li className="mobile-sidebar__divider" />
          <li className="mobile-sidebar__item" onClick={sidebarAction(() =>
            sendAppState({ type: 'createNotebook', data: { notebookName: 'New Notebook' } })
          )}>
            <i className="material-icons">library_add</i> New Notebook
          </li>
          <li className="mobile-sidebar__item mobile-sidebar__item--primary" onClick={sidebarAction(() => {
            const notebookIdToUse = selectedNotebookId === 'all_notes'
              ? defaultNotebook?.notebook_id
              : selectedNotebookId;
            if (pathname !== '/') router.push('/');
            sendAppState({ type: 'SelectTab', tabName: 'editorWrapper' });
            sendAppState({ type: 'createNote', data: {
              noteObject: { ...newNoteEntityObject, notebook_id: notebookIdToUse },
              selectedAreaOfFocus,
              openNotesLength: selectedAreaOfFocus?.order_of_items?.length ?? 0,
              isSelectedAreaOfFocus: false,
            }});
          })}>
            <i className="material-icons">note_add</i> New Note
          </li>
        </ul>
      </nav>
    </>,
    document.body
  );

  return (
    <>
      <footer className={`mobile-actions${keyboardVisible ? ' mobile-actions--keyboard-open' : ''}`}>
        <a className={`action${pathname === '/home' ? ' selected' : ''}`} onClick={goHome}>
          <i className="material-icons">home</i>
        </a>
        <a className={`action${showOpenNotesPanel ? ' selected' : ''}`} onClick={toggleOpenNotes}>
          <span className="open-notes-icon-wrap">
            <i className="material-icons">tab</i>
            {openNotesCount > 0 && (
              <span className="open-notes-badge">{openNotesCount}</span>
            )}
          </span>
        </a>
        {/* Search temporarily disabled to reduce costs */}
        {/* <a className="action" onClick={openSearch}>
          <i className="material-icons">search</i>
        </a> */}
        <button className="action action--fab" onClick={addNote} aria-label="New note">
          <i className="material-icons">edit</i>
        </button>
        <a className={`action${formattingBarOpen ? ' selected' : ''}`} onClick={() => setFormattingBarOpen(v => !v)}>
          <i className="material-icons">text_format</i>
        </a>
        <a className={`action${sidebarOpen ? ' selected' : ''}`} onClick={() => setSidebarOpen(true)}>
          <i className="material-icons">menu</i>
        </a>
        {keyboardVisible && (
          <a className="action" onClick={() => document.activeElement?.blur()} aria-label="Dismiss keyboard">
            <i className="material-icons">keyboard_hide</i>
          </a>
        )}
      </footer>
      {formattingBar}
      {sidebar}
    </>
  );
};

export default MobileActionsMenu;
